import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { getSupabase } from '@/lib/supabaseClient';

function NewsletterSignup({ className, isContactPage = false }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { toast } = useToast();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    // Frontend validation
    if (!name.trim() || !email.trim()) {
      setErrorMessage(t('newsletter.requiredFields', 'Name and email are required.'));
      setIsLoading(false);
      return;
    }

    const supabase = getSupabase();
    if (!supabase) {
      setErrorMessage(t('newsletter.serverError', 'Server error. Please try again later.'));
      setIsLoading(false);
      return;
    }

    try {
      // Insert newsletter signup into Supabase
      const { error } = await supabase
        .from('newsletter_signups')
        .insert([
          {
            name: name.trim(),
            email: email.trim().toLowerCase(),
            created_at: new Date().toISOString()
          }
        ]);

      if (error) {
        // Check if it's a duplicate email error
        if (error.code === '23505' || error.message?.includes('unique')) {
          setErrorMessage(t('newsletter.duplicateEmail', 'This email is already subscribed.'));
        } else {
          console.error('Newsletter signup error:', error);
          setErrorMessage(t('newsletter.errorMessage', 'Failed to subscribe. Please try again.'));
        }
        setIsLoading(false);
        return;
      }

      // Success
      toast({
        title: t('newsletter.success', 'Success!'),
        description: t('newsletter.successMessage', 'You have been subscribed to our newsletter.'),
      });
      setName('');
      setEmail('');
      setErrorMessage('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setErrorMessage(error.message || t('newsletter.errorMessage', 'Failed to subscribe. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      {!isContactPage && (
        <>
          <h3 className="mb-2 text-lg font-semibold text-foreground">
            {t('newsletter.title', 'Stay Updated')}
          </h3>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('newsletter.description', 'Subscribe to our newsletter for the latest financial tips and updates.')}
          </p>
        </>
      )}
      <form onSubmit={handleSubmit} className="newsletter-signup-form">
        <div className={`flex ${isContactPage ? 'flex-col' : 'flex-col md:flex-row'} gap-2`}>
          <div className="flex-1">
            <label htmlFor="newsletter-name" className="block mb-1 text-sm font-medium text-foreground">
              {t('newsletter.nameLabel', 'Name')} <span className="text-destructive">*</span>
            </label>
            <Input
              id="newsletter-name"
              type="text"
              placeholder={t('newsletter.namePlaceholder', 'Your name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="flex-1"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="newsletter-email" className="block mb-1 text-sm font-medium text-foreground">
              {t('newsletter.emailLabel', 'Email')} <span className="text-destructive">*</span>
            </label>
            <Input
              id="newsletter-email"
              type="email"
              placeholder={t('newsletter.emailPlaceholder', 'Your email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1"
            />
          </div>
          <Button type="submit" disabled={isLoading} className="self-end mt-6 whitespace-nowrap md:mt-0">
            {isLoading ? t('newsletter.subscribing', 'Subscribing...') : t('newsletter.subscribe', 'Subscribe')}
          </Button>
        </div>
        {errorMessage && (
          <div className="mt-2 text-sm text-center text-destructive">{errorMessage}</div>
        )}
      </form>
      <p className="mt-2 text-xs text-muted-foreground">
        {t('newsletter.privacyNotice', 'We respect your privacy. Your information will only be used for sending updates and will never be sold or shared with third parties.')}
      </p>
    </div>
  );
}

export default NewsletterSignup; 